<template>
  <section id="hero" class="hero">
    <div class="hero__content">
      <p class="hero__eyebrow reveal" :class="{ 'is-visible': mounted }">
        <span class="hero__eyebrow-dot"></span>
        Data Engineer · Open to Opportunities
      </p>

      <h1 class="hero__name reveal reveal--d1" :class="{ 'is-visible': mounted }">
        Dixit<br />
        <span class="gradient-text">Prajapati</span>
      </h1>

      <p class="hero__role reveal reveal--d2" :class="{ 'is-visible': mounted }">
        <span class="hero__role-prefix">I build</span>
        <span class="hero__typewriter">
          {{ displayText }}<span class="hero__cursor">|</span>
        </span>
      </p>

      <p class="hero__bio reveal reveal--d3" :class="{ 'is-visible': mounted }">
        I build the data infrastructure that businesses run on — scalable
        pipelines, cloud-native architectures, and reliable ETL systems
        engineered to handle millions of events daily.
      </p>

      <div class="hero__cta reveal reveal--d4" :class="{ 'is-visible': mounted }">
        <button class="btn btn--primary" @click="scrollTo('contact')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          Let's Work Together
        </button>
        <button class="btn btn--outline" @click="scrollTo('resume')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          View Resume
        </button>
      </div>

      <!-- Stats strip -->
      <div class="hero__stats reveal reveal--d5" :class="{ 'is-visible': mounted }">
        <div class="hero__stat" v-for="stat in stats" :key="stat.label">
          <span class="hero__stat-value gradient-text">{{ stat.value }}</span>
          <span class="hero__stat-label">{{ stat.label }}</span>
        </div>
      </div>
    </div>

    <!-- Right decorative panel -->
    <div class="hero__visual reveal reveal--right reveal--d3" :class="{ 'is-visible': mounted }">
      <div class="hero__code-card">
        <div class="hero__code-header">
          <span class="dot dot--red"></span>
          <span class="dot dot--yellow"></span>
          <span class="dot dot--green"></span>
          <span class="hero__code-filename">pipeline.py</span>
        </div>
        <pre class="hero__code"><code><span class="c-keyword">import</span> <span class="c-module">apache_beam</span> <span class="c-keyword">as</span> beam
<span class="c-keyword">from</span> <span class="c-module">pyspark.sql</span> <span class="c-keyword">import</span> SparkSession

<span class="c-comment"># Initialize Spark session</span>
spark <span class="c-op">=</span> SparkSession<span class="c-op">.</span>builder<span class="c-op">.</span><span class="c-fn">appName</span>(
    <span class="c-str">"ETL_Pipeline"</span>
)<span class="c-op">.</span><span class="c-fn">getOrCreate</span>()

<span class="c-comment"># Read from Azure Data Lake</span>
df <span class="c-op">=</span> spark<span class="c-op">.</span>read<span class="c-op">.</span><span class="c-fn">parquet</span>(
    <span class="c-str">"abfss://data@lake.dfs.core.windows.net/"</span>
)

<span class="c-comment"># Transform & load to Synapse</span>
df<span class="c-op">.</span><span class="c-fn">write</span>()\
  <span class="c-op">.</span><span class="c-fn">mode</span>(<span class="c-str">"overwrite"</span>)\
  <span class="c-op">.</span><span class="c-fn">saveAsTable</span>(<span class="c-str">"gold.analytics"</span>)</code></pre>
      </div>

      <!-- Floating tech badges -->
      <div class="hero__badges">
        <div class="hero__badge hero__badge--1">🔷 Azure ADF</div>
        <div class="hero__badge hero__badge--2">⚡ Apache Spark</div>
        <div class="hero__badge hero__badge--3">🐍 Python</div>
        <div class="hero__badge hero__badge--4">☁️ AWS Glue</div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="hero__scroll reveal reveal--d6" :class="{ 'is-visible': mounted }" @click="scrollTo('about')">
      <div class="hero__scroll-line"></div>
      <span>Scroll</span>
    </div>
  </section>
</template>

<script setup>
import { ref, inject, onMounted, onUnmounted } from 'vue'

const mounted = ref(false)
const displayText = ref('')
let running = false

const navigateTo = inject('navigateTo', (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }))

const roles = [
  'scalable data pipelines.',
  'cloud-native ETL systems.',
  'real-time data streams.',
  'enterprise data warehouses.',
  'production Spark workflows.',
]

function scrollTo(id) {
  navigateTo(id)
}

const stats = [
  { value: '4+', label: 'Years Experience' },
  { value: '12+', label: 'Certifications' },
  { value: '10+', label: 'Projects Shipped' },
  { value: '20+', label: 'Tech Mastered' },
]

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function typewriter() {
  let idx = 0
  while (running) {
    const role = roles[idx % roles.length]
    for (let i = 0; i <= role.length; i++) {
      displayText.value = role.slice(0, i)
      await sleep(75)
    }
    await sleep(2200)
    for (let i = role.length; i >= 0; i--) {
      displayText.value = role.slice(0, i)
      await sleep(35)
    }
    await sleep(400)
    idx++
  }
}

onMounted(() => {
  running = true
  requestAnimationFrame(() => { mounted.value = true })
  typewriter()
})

onUnmounted(() => { running = false })
</script>

<style scoped>
.hero {
  height: 100%;
  display: flex;
  align-items: center;
  padding: 40px 60px 40px;
  gap: 60px;
  position: relative;
  overflow: hidden;
}

/* Background: subtle grid + radial glow for depth */
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 212, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 212, 255, 0.03) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
}

.hero__content { flex: 1; max-width: 560px; }

.hero__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--color-accent);
  font-family: var(--font-mono);
  margin-bottom: 24px;
  letter-spacing: 0.08em;
  background: rgba(0, 212, 255, 0.07);
  border: 1px solid rgba(0, 212, 255, 0.2);
  padding: 6px 14px;
  border-radius: 100px;
}
.hero__eyebrow-dot {
  width: 8px; height: 8px;
  background: var(--color-accent);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }

.hero__name {
  font-size: clamp(2.2rem, 4.5vw, 3.8rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  margin-bottom: 16px;
  color: var(--color-text);
}

.hero__role {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: clamp(1.25rem, 2.5vw, 1.625rem);
  font-weight: 600;
  margin-bottom: 24px;
  flex-wrap: wrap;
  min-height: 2em;
}
.hero__role-prefix { color: var(--color-text-secondary); font-weight: 400; }
.hero__typewriter {
  color: var(--color-accent);
  font-family: var(--font-mono);
  font-size: 0.95em;
}
.hero__cursor {
  animation: blink 1s step-end infinite;
  margin-left: 1px;
  color: var(--color-purple);
}
@keyframes blink { 0%,100% { opacity: 1; } 50% { opacity: 0; } }

.hero__bio {
  font-size: 0.9875rem;
  color: var(--color-text-secondary);
  line-height: 1.65;
  margin-bottom: 24px;
  max-width: 480px;
}

.hero__cta {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}

.hero__stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  padding-top: 32px;
  border-top: 1px solid var(--color-border);
}
.hero__stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 24px;
  border-right: 1px solid var(--color-border);
  padding-left: 0;
}
.hero__stat:first-child { padding-left: 0; }
.hero__stat:last-child  { border-right: none; padding-right: 0; }
.hero__stat + .hero__stat { padding-left: 24px; }
.hero__stat-value {
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
}
.hero__stat-label {
  font-size: 10px;
  color: var(--color-text-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  white-space: nowrap;
}

/* Visual / code card */
.hero__visual {
  flex: 1;
  max-width: 480px;
  position: relative;
}
.hero__code-card {
  background: rgba(8, 8, 20, 0.9);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(0, 212, 255, 0.08);
}
.hero__code-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: rgba(255,255,255,0.03);
  border-bottom: 1px solid var(--color-border);
}
.dot { width: 12px; height: 12px; border-radius: 50%; }
.dot--red { background: #ff5f57; }
.dot--yellow { background: #ffbd2e; }
.dot--green { background: #28c840; }
.hero__code-filename {
  margin-left: 8px;
  font-size: 12px;
  font-family: var(--font-mono);
  color: var(--color-text-muted);
}
.hero__code {
  padding: 20px;
  font-family: var(--font-mono);
  font-size: 12.5px;
  line-height: 1.8;
  overflow-x: auto;
  margin: 0;
}
.hero__code code { display: block; }

/* Code syntax colors */
.c-keyword { color: #c792ea; }
.c-module  { color: #82aaff; }
.c-fn      { color: #82aaff; }
.c-str     { color: #c3e88d; }
.c-comment { color: #546e7a; font-style: italic; }
.c-op      { color: var(--color-text-secondary); }

/* Floating badges */
.hero__badges { position: absolute; right: -20px; top: -20px; }
.hero__badge {
  position: absolute;
  background: rgba(8, 8, 20, 0.95);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  animation: float-badge 4s ease-in-out infinite;
  box-shadow: var(--shadow);
}
.hero__badge--1 { top: -16px; right: 24px; animation-delay: 0s; }
.hero__badge--2 { bottom: 80px; right: -24px; animation-delay: 1s; }
.hero__badge--3 { bottom: 160px; right: -32px; animation-delay: 2s; }
.hero__badge--4 { bottom: 20px; left: -24px; animation-delay: 0.5s; }
@keyframes float-badge {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

/* Scroll indicator */
.hero__scroll {
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  opacity: 0.5;
  transition: var(--transition);
}
.hero__scroll:hover { opacity: 1; }
.hero__scroll-line {
  width: 1px; height: 48px;
  background: linear-gradient(to bottom, var(--color-accent), transparent);
  animation: scroll-line 2s ease-in-out infinite;
}
@keyframes scroll-line {
  0% { opacity: 0; transform: scaleY(0); transform-origin: top; }
  50% { opacity: 1; transform: scaleY(1); }
  100% { opacity: 0; transform: scaleY(1); transform-origin: top; }
}
.hero__scroll span {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
}

@media (max-width: 1100px) {
  .hero { flex-direction: column; padding: 60px 40px; gap: 48px; align-items: flex-start; }
  .hero__visual { display: none; }
  .hero__scroll { display: none; }
}
@media (max-width: 768px) {
  .hero { padding: 24px 20px 40px; min-height: 0; }
  .hero__name { margin-bottom: 16px; }
  .hero__bio { font-size: 0.9375rem; margin-bottom: 28px; }
  .hero__cta { gap: 10px; margin-bottom: 32px; }
  .hero__stats { grid-template-columns: repeat(2, 1fr); gap: 20px; padding-top: 24px; }
  .hero__stat { border-right: none !important; padding-right: 0 !important; padding-left: 0 !important; border-bottom: 1px solid var(--color-border); padding-bottom: 16px; }
  .hero__stat:nth-child(3), .hero__stat:nth-child(4) { border-bottom: none; padding-bottom: 0; }
  .hero__stat-value { font-size: 1.5rem; }
}
@media (max-width: 480px) {
  .hero { padding: 40px 16px 32px; }
  .hero__eyebrow { font-size: 11px; }
  .hero__role { font-size: 1.125rem; gap: 6px; }
  .hero__cta { flex-direction: column; }
  .hero__cta .btn { width: 100%; justify-content: center; }
  .hero__stats { gap: 16px; }
  .hero__stat-value { font-size: 1.375rem; }
}
</style>
